"use client";

import { IconCaretDown } from "@decibeltrade/react-icons";
import { focusRing } from "@decibeltrade/web-core/recipes";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { css, cva, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { useAnalytics } from "@/contexts/analytics-context";
import { useViewAs } from "@/contexts/view-as-context";

import { Clickable } from "../clickable";
import { openViewAsModal } from "../modal/view-as-modal";
import { Popover } from "../popover";
import { getIsPathActive, useNavItems } from "./nav-items";
import { NewBadge } from "./new-badge";
import { RewardsCountBadge } from "./rewards-count-badge";

export interface DesktopNavRowProps {
  className?: string;
}

export function DesktopNavRow({ className }: DesktopNavRowProps) {
  const t = useTranslations("navItems");
  const pathname = usePathname();
  const navItems = useNavItems();
  const { isViewAsAllowed } = useViewAs();
  const analytics = useAnalytics();

  const handleCardVaultNavClick = useCallback(() => {
    analytics.cardVault.navTabClicked();
  }, [analytics]);

  return (
    <div className={cx(flex({ alignItems: "center", flex: "1", gap: "24" }), className)}>
      {navItems.map((item, index) => {
        if ("options" in item) {
          return (
            <Popover
              trigger={
                <Clickable
                  key={index}
                  className={cx(linkStyles(), focusRing(), popoverTriggerStyles)}
                >
                  {t(item.tx)}
                  <IconCaretDown className={popoverCaretStyles} />
                </Clickable>
              }
              key={item.tx}
            >
              {({ setIsOpen }) => (
                <div className={popoverStyles}>
                  {item.options.map((option) => {
                    const isActive = getIsPathActive(pathname, option.path);
                    const isCardVault = option.tx === "cardVault";
                    return (
                      <Link
                        key={option.tx}
                        href={option.path}
                        target={option.newTab ? "_blank" : undefined}
                        onClick={() => {
                          setIsOpen(false);
                          if (isCardVault) handleCardVaultNavClick();
                        }}
                        className={cx(
                          linkStyles({ variant: isActive ? "active" : "inactive" }),
                          focusRing(),
                          option.desktopOnly && desktopOnlyStyles,
                        )}
                      >
                        {t(option.tx)}
                      </Link>
                    );
                  })}
                  {isViewAsAllowed && (
                    <Clickable
                      onClick={() => {
                        setIsOpen(false);
                        openViewAsModal();
                      }}
                      className={cx(
                        linkStyles({ variant: "inactive" }),
                        focusRing(),
                        css({ textAlign: "left" }),
                      )}
                    >
                      {t("viewAsUser")}
                    </Clickable>
                  )}
                </div>
              )}
            </Popover>
          );
        }
        const isActive = getIsPathActive(pathname, item.path);
        return (
          <Link
            key={item.tx}
            target={item.newTab ? "_blank" : undefined}
            className={cx(
              linkStyles({ variant: isActive ? "active" : "inactive" }),
              focusRing(),
              item.desktopOnly && desktopOnlyStyles,
            )}
            href={item.path}
          >
            {t(item.tx)}
            {item.badge === "new" && <NewBadge label={t("newBadge")} />}
            {item.tx === "rewards" && <RewardsCountBadge />}
          </Link>
        );
      })}
    </div>
  );
}

const linkStyles = cva({
  base: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    textDecoration: "none",
    color: "text.tertiary",
    textStyle: "highlight.md",
    transitionPreset: "colors",
    whiteSpace: "nowrap",
    _hover: {
      color: "text.primary",
    },
  },
  variants: {
    variant: {
      active: {
        color: "text.secondary",
      },
      inactive: {
        color: "text.tertiary",
      },
    },
  },
});

const desktopOnlyStyles = css({ hideBelow: "desktop" });

const popoverTriggerStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "4",
  "&[data-state='open'] svg": {
    transform: "rotate(180deg)",
  },
  "&[data-state='closed'] svg": {
    transform: "rotate(0deg)",
  },
});

const popoverCaretStyles = css({
  w: "16",
  h: "16",
  transitionPreset: "transform",
});

const popoverStyles = stack({
  gap: "16",
  p: "8",
  bg: "background.primary",
  rounded: "200",
  shadow: "default",
  borderWidth: "100",
  borderColor: "border.secondary",
});
